import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { getDealersByVicinityId } from '@yme/api';
import { AlertType } from '@yme/atoms/alert';
import { Dropdown } from '@yme/atoms/dropdown';
import { Col, Row } from '@yme/atoms/grid';
import { Pagination } from '@yme/atoms/pagination';
import { SpinnerLoader } from '@yme/atoms/spinner-loader';
import { useWindowSize } from '@yme/common/hooks';
import { SectionWrapper } from '@yme/molecules/section-wrapper';
import { useTranslation } from '@yme/services/i18n';
import { DigitalDataMutation, buildKey, buildStateParam, setDigitalData } from '@yme/utils/common';
import { TestRideContent } from './TestRideContent';
import { DRIVER_LICENCE_CONFIG, HEADER_MARGIN, LICENSE_DROPDOWN_LABEL, TEST_RIDE_WRAPPER_ID, } from './constants';
import { AlertStyled, AlertWrapper, DropdownMobileWrapperStyled, ShowAllDealersButton, TestRideGridWrapperStyled, } from './styled';
import { useTestRideGrid } from './useTestRideGrid';
import { useTestRideIframeError } from './useTestRideIframeError';
export function TestRideGrid({ productData, testRideFormLinks, isLoading, isLoaded, isError, isRightToLeft, isShowDriverLicenseFilter, languageRootPath, isInIframeError, isInEditor, dealerIdList, }) {
    const [dealerName, setDealerName] = useState('');
    const { t } = useTranslation();
    const { isMobileView, isInfiniteDesktopView } = useWindowSize();
    const history = useHistory();
    useEffect(() => {
        getDealersByVicinityId(dealerIdList).then((data) => {
            const dealerNames = data.map((dealer) => dealer.name);
            setDealerName(dealerNames.join(', '));
        });
    }, [dealerIdList]);
    const { config, demoUnits, paginationConfigData, paginationConfig, activeItemValue, onClickItemHandler, setCurrentPerPageValue, filterOnChangeHandler, } = useTestRideGrid({
        isShowDriverLicenseFilter,
        isMobileView,
        productData,
    });
    useTestRideIframeError({
        isInIframeError,
        languageRootPath,
    });
    const onItemClicked = useCallback((sku, url) => {
        setDigitalData(DigitalDataMutation.TEST_RIDE_SELECT_PRODUCT, {
            sku,
            url,
        });
    }, []);
    const LicenseDropdown = useMemo(() => (React.createElement(DropdownMobileWrapperStyled, null,
        React.createElement(Dropdown, { id: buildKey(LICENSE_DROPDOWN_LABEL, buildStateParam()), activeItem: config.driverLicense, items: DRIVER_LICENCE_CONFIG, isRightToLeft: isRightToLeft, contents: true, onChange: filterOnChangeHandler, isShowIconThenListOpened: true, useForMobile: true }))), [config.driverLicense, filterOnChangeHandler, isRightToLeft]);
    const handleShowAllDialers = useCallback(() => {
        const params = new URLSearchParams(history.location.search);
        ['dealerIds', 'dealerId'].forEach((param) => {
            params.delete(param);
        });
        history.replace({
            pathname: location.pathname,
            search: params.toString(),
            hash: location.hash,
        });
    }, [history]);
    const ShowAllDialersAlert = useMemo(() => (React.createElement(AlertStyled, { isInfiniteDesktopView: isInfiniteDesktopView, isFullWidth: true, message: t('productsGrid_selectedDealer_label') + dealerName, type: AlertType.INFO },
        React.createElement(ShowAllDealersButton, { onClick: handleShowAllDialers }, t('productsGrid_showAllDealersVehicles_label')))), [dealerName, handleShowAllDialers, isInfiniteDesktopView, t]);
    const isShowAllDialersAlertVisible = useMemo(() => {
        return isShowDriverLicenseFilter
            ? Boolean(dealerIdList.length)
            : Boolean(!isInfiniteDesktopView && dealerIdList.length);
    }, [dealerIdList.length, isInfiniteDesktopView, isShowDriverLicenseFilter]);
    const rightSideContent = useMemo(() => {
        return isShowDriverLicenseFilter
            ? LicenseDropdown
            : isInfiniteDesktopView && Boolean(dealerIdList.length)
                ? ShowAllDialersAlert
                : undefined;
    }, [
        LicenseDropdown,
        ShowAllDialersAlert,
        dealerIdList.length,
        isInfiniteDesktopView,
        isShowDriverLicenseFilter,
    ]);
    return (React.createElement(TestRideGridWrapperStyled, { id: TEST_RIDE_WRAPPER_ID },
        React.createElement(SectionWrapper, { headerLeftSide: t('productsGrid_chooseModel_header'), isRightToLeft: isRightToLeft, width: "100%", maxWidth: "1440px", isVerticalIndentDefault: false, isExpanded: false, blockPosition: "relative", desktopRatio: {
                leftSide: 4,
                rightSide: 8,
            }, headerRightSide: rightSideContent },
            isShowAllDialersAlertVisible && React.createElement(AlertWrapper, null, ShowAllDialersAlert),
            React.createElement(TestRideContent, { isRightToLeft: isRightToLeft, isError: isError, isInEditor: isInEditor, onClick: onItemClicked, isLoaded: isLoaded, demoUnits: demoUnits, testRideFormLinks: testRideFormLinks, languageRootPath: languageRootPath, currentPerPageValue: Number(paginationConfigData.activeElement) }),
            React.createElement(Row, { mt: "24px" },
                React.createElement(Col, null, isLoaded && Boolean(config) && (React.createElement(Pagination
                // common props
                , { 
                    // common props
                    isRightToLeft: isRightToLeft, isMobileView: isMobileView, 
                    // Pagination props
                    paginationData: paginationConfigData, paginationConfig: paginationConfig, activeItemValue: activeItemValue, onClickItemHandler: onClickItemHandler, 
                    // PerPage props
                    perPageLabel: t('filters_itemsPerPage_label'), currentPerPageValue: config.perPage, setCurrentPerPageValue: setCurrentPerPageValue, scrollToElement: {
                        id: TEST_RIDE_WRAPPER_ID,
                        yOffset: HEADER_MARGIN,
                    } })))),
            React.createElement(SpinnerLoader, { isLoading: isLoading }))));
}
TestRideGrid.displayName = 'TestRideGrid';
